import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const PROJECTS = [
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/ksiudzqh_hammersley_stickroof.jpg",
    alt: "Completed stick roof and framing on a new Perth home",
    title: "Hammersley",
    tag: "Stick Roof",
    span: "md:col-span-4",
    height: "h-72 md:h-96",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/zcunkapw_woolcott_framing.jpg",
    alt: "Interior timber wall framing on a new build",
    title: "Woolcott",
    tag: "Timber Framing",
    span: "md:col-span-2",
    height: "h-72 md:h-96",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/qgdot65f_woolcott.jpg",
    alt: "New Perth home with roof structure completed",
    title: "Woolcott",
    tag: "New Build",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/jmyq5i3w_flecker_49.jpg",
    alt: "Roof trusses installed over brickwork on a Perth build",
    title: "Flecker 49",
    tag: "Roof Trusses",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/xz9u7z13_woolcott_porch.jpg",
    alt: "Finished porch ceiling with structural post",
    title: "Woolcott",
    tag: "Porch & Structural",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/5jqhp2yi_869216b4-9aa5-4125-b34d-874f70d7cfbc.jpg",
    alt: "Street view of a new Perth home with roof trusses installed",
    title: "Roof On",
    tag: "Roof Trusses",
    span: "md:col-span-3",
    height: "h-72 md:h-96",
  },
  {
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/1x6hg5kw_700d51a9-d03c-4179-accd-9e47138b4d3f.jpg",
    alt: "Long interior view of timber wall and roof framing on a concrete slab",
    title: "Inside The Frame",
    tag: "Timber Framing",
    span: "md:col-span-3",
    height: "h-72 md:h-96",
  },
  {
    img: "/gallery/photo08.jpg",
    alt: "View from the roof over new trusses and neighbouring homes",
    title: "Up Top",
    tag: "Roof Trusses",
    span: "md:col-span-3",
    height: "h-72 md:h-96",
  },
  {
    img: "/gallery/photo09.jpg",
    alt: "Looking across newly installed roof battens and trusses",
    title: "From The Roof",
    tag: "Stick Roof",
    span: "md:col-span-3",
    height: "h-72 md:h-96",
  },
  {
    img: "/gallery/photo12.jpg",
    alt: "Interior timber wall and roof framing ready for linings",
    title: "Frames Up",
    tag: "Timber Framing",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
  {
    img: "/gallery/photo11.jpg",
    alt: "Close-up of a cut rafter and carpenter's drill on site",
    title: "The Detail",
    tag: "Carpentry Detail",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
  {
    img: "/gallery/photo10.jpg",
    alt: "Timber packs on brickwork at sunrise, ready for the roof",
    title: "Ready For Roof",
    tag: "New Build",
    span: "md:col-span-2",
    height: "h-72 md:h-80",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Gallery() {
  const [openIndex, setOpenIndex] = useState(null);

  const close = () => setOpenIndex(null);
  const step = (dir) =>
    setOpenIndex((i) => (i + dir + PROJECTS.length) % PROJECTS.length);

  useEffect(() => {
    if (openIndex === null) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [openIndex]);

  return (
    <section id="gallery" data-testid="gallery-section" className="border-b border-neutral-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={reveal}
          custom={0}
          className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-timber mb-4">Recent Work</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl text-white leading-[0.9]">
              Proof, not promises.<br />
              <span className="text-outline">Straight off site.</span>
            </h2>
          </div>
          <p className="text-neutral-400 text-base max-w-sm leading-relaxed">
            Real roofs and frames from recent Perth jobs — no stock photos, no
            filters. This is what turns up when we do.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-6 border-t border-l border-neutral-800">
          {PROJECTS.map((p, i) => (
            <motion.figure
              key={`${p.title}-${i}`}
              data-testid={`gallery-item-${i + 1}`}
              onClick={() => setOpenIndex(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setOpenIndex(i)}
              aria-label={`Open photo: ${p.alt}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={reveal}
              custom={i + 1}
              className={`group relative border-r border-b border-neutral-800 bg-surface overflow-hidden cursor-pointer ${p.span}`}
            >
              <div className={`overflow-hidden ${p.height}`}>
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700 ease-out"
                />
              </div>
              <figcaption className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6 bg-gradient-to-t from-black/80 to-transparent">
                <span className="block font-display text-timber text-sm font-bold tracking-widest uppercase">{p.tag}</span>
                <span className="font-display text-white/20 text-4xl font-black">{String(i + 1).padStart(2, "0")}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIndex !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close photo"
              className="absolute top-5 right-5 text-neutral-400 hover:text-timber transition-colors duration-300 p-2"
            >
              <X size={32} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-timber transition-colors duration-300 p-2"
            >
              <ChevronLeft size={44} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-timber transition-colors duration-300 p-2"
            >
              <ChevronRight size={44} />
            </button>
            <motion.figure
              key={openIndex}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              <img
                data-testid="lightbox-image"
                src={PROJECTS[openIndex].img}
                alt={PROJECTS[openIndex].alt}
                className="w-full max-h-[80vh] object-contain"
              />
              <figcaption className="mt-4 flex items-center justify-between">
                <span className="font-display text-timber text-sm font-bold tracking-widest uppercase">
                  {PROJECTS[openIndex].tag}
                </span>
                <span className="text-neutral-500 text-sm tracking-widest">
                  {openIndex + 1} / {PROJECTS.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
